import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet } from 'react-router-dom';

import { ErrorStateFallback } from '@/components/ui/error-state';
import { Header } from '@/components/ui/header';
import { Title } from '@/components/ui/title';
import { Typography } from '@/components/ui/typography';
import {
  TableProvider,
  TableQueryParamsConfig,
} from '@/contexts/table-context';

import { CampaignsTable } from './campaigns-table';
import { Filters } from './filters';
import { FreemiumMigrationBanner } from './freemium-migration-banner';
import { HiresToDateProvider } from './hires-to-date/context';
import { CampaignsMetrics } from './metrics';
import { PostJobButton } from './post-job-button';

// Scoped to this table so changing the campaigns rows-per-page preference
// never leaks into another table (e.g. Dashboard) sharing `TableProvider`.
const ROWS_PER_PAGE_STORAGE_KEY = 'source.campaigns.rowsPerPage';
// Keeps page/search/filters/sort/date-range in the URL so the browser's back
// button returns to the same view after navigating away, and so the current
// view is shareable via URL. Declared once at module scope so the config
// object identity is stable across renders. Shared by the Filters bar,
// CampaignsMetrics, and CampaignsTable via a single TableProvider below.
const QUERY_PARAMS: TableQueryParamsConfig = {
  page: 'page',
  search: 'q',
  sortBy: 'sortBy',
  sortDirection: 'sortDir',
  filters: {
    channelIds: 'channels',
    statuses: 'statuses',
    positionIds: 'positions',
    locationIds: 'locations',
    brandIds: 'brands',
  },
  dateRange: {
    preset: 'duration',
    startDate: 'from',
    endDate: 'to',
  },
};

export function Campaigns() {
  const intl = useIntl();

  return (
    <main className="flex flex-1 flex-col gap-4 px-6 py-2">
      <ErrorStateFallback>
        <Header
          headers={[
            {
              title: intl.formatMessage({
                defaultMessage: 'Campaigns',
                description: 'Breadcrumb text for the all-campaigns list view',
              }),
            },
          ]}
        />
        <FreemiumMigrationBanner />
        <div className="flex items-center gap-1">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Title
              title={intl.formatMessage({
                defaultMessage: 'All Campaigns',
                description: 'Page title for the org-wide campaigns list',
              })}
            />
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="Review, monitor, and manage every campaign running across your sourcing channels."
                description="Page subtitle explaining the purpose of the all-campaigns list"
              />
            </Typography>
          </div>
          <PostJobButton />
        </div>
        <TableProvider
          pageSizeStorageKey={ROWS_PER_PAGE_STORAGE_KEY}
          queryParams={QUERY_PARAMS}
        >
          <Filters />
          <HiresToDateProvider>
            <CampaignsMetrics />
            <CampaignsTable />
          </HiresToDateProvider>
        </TableProvider>

        {/* Renders the edit-campaign dialog as an overlay when the URL
            matches the nested :channelSlug/:sourcingPurchaseId/edit route
            (see routes.tsx). Reach uses the opening's full-page editor
            (`dashboard/:openingSlug/channels/reach/:id/edit`) instead. */}
        <Outlet />
      </ErrorStateFallback>
    </main>
  );
}
